<template>
  <div class="container" v-if="claim && parcel">
    <div class="box-title" style="display:flex;justify-content: space-between">
      <h4>
        <i class="fal fa-balance-scale"></i>
        ປະເມີນພັດສະດຸ {{ parcel.shipment ? parcel.shipment.shipment_number : '' }}
        <span class="claim-of">ໃນຄຳຮ້ອງ {{ claim.claim_no }}</span>
      </h4>
      <button class="button is-dark" @click="$router.back()">ກັບຄືນ</button>
    </div>

    <div class="columns is-multiline">
      <!-- ── what is being decided against ───────────────────────────── -->
      <div class="column is-5">
        <div class="box">
          <h5 class="claim-section">ຂໍ້ມູນປະກອບການຕັດສິນ</h5>
          <table class="table is-fullwidth">
            <tbody>
              <tr><th>ເລກບິນ</th><td>{{ parcel.shipment ? parcel.shipment.shipment_number : '-' }}</td></tr>
              <!--
                Which of the claimant's boxes this is. A claim can hold several, each assessed on
                its own evidence and carried by its own party — this decision is about ONE of them.
              -->
              <tr v-if="claim.items.length > 1">
                <th>ພັດສະດຸ</th>
                <td>{{ parcelPosition }} ຈາກ {{ claim.items.length }} ໃນຄຳຮ້ອງນີ້</td>
              </tr>
              <!--
                The claimant's own figure, beside the one being decided (task 2.4).
                It is what a disagreement is argued over, and an assessment that
                looks like it replaced it is how a dispute becomes about trust.
              -->
              <tr>
                <th>ລູກຄ້າຂໍ</th>
                <td class="claim-asked">{{ formatMoney(parcel.claimed_amount) }} ກີບ</td>
              </tr>
              <!--
                The parcel's insurance figure (task 2.3). The backend captures it
                on every claim precisely so this decision is made against it
                rather than against memory.
              -->
              <tr>
                <th>ຄ່າປະກັນໄພຂອງບິນ</th>
                <td>{{ formatMoney(parcel.insured_amount) }} ກີບ</td>
              </tr>
              <!--
                The COD, because on a COD basis it IS the approved amount and
                the amount field goes disabled — without this row the officer is
                asked to approve a figure the screen never showed them.
              -->
              <tr>
                <th>ຄ່າ COD ຂອງບິນ</th>
                <td :class="{ 'claim-muted': !hasCod }">
                  <span v-if="hasCod">{{ formatMoney(codAmount) }} ກີບ</span>
                  <span v-else>ບິນນີ້ບໍ່ແມ່ນບິນ COD</span>
                </td>
              </tr>
              <!-- COD + freight already paid at the origin: the COD basis pays both back. -->
              <tr v-if="hasCod && originFreight > 0">
                <th>ຄ່າຂົນສົ່ງທີ່ຈ່າຍຕົ້ນທາງແລ້ວ</th>
                <td>
                  {{ formatMoney(originFreight) }} ກີບ
                  <span class="claim-muted">· ລວມຊົດເຊີຍຖານ COD {{ formatMoney(codValuation) }} ກີບ</span>
                </td>
              </tr>
              <tr><th>ເຫດຜົນທີ່ລູກຄ້າແຈ້ງ</th><td>{{ parcel.description }}</td></tr>
              <tr>
                <th>ຮູບ / ເອກະສານແນບ</th>
                <td>{{ (parcel.attachments || []).length }} ໄຟລ໌</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── the assessment, decided in one submit ───────────────────── -->
      <div class="column is-7">
        <div class="box">
          <h5 class="claim-section">ຜົນການປະເມີນ</h5>

          <div class="columns is-multiline">
            <!-- who carries the cost: the paper form's question, with its own answers -->
            <div class="column is-6">
              <div class="field">
                <div class="control">
                  <label class="label">
                    ຝ່າຍທີ່ຮັບຜິດຊອບ
                    <span class="text-danger">* {{ errors.first('liable_party') }}</span>
                    <span class="text-danger">{{ server_errors.liable_party }}</span>
                  </label>
                  <select
                    class="input"
                    name="liable_party"
                    v-model="form.liable_party"
                    v-validate="'required'"
                    @change="onLiablePartyChanged"
                  >
                    <option value="">-- ເລືອກ --</option>
                    <option v-for="o in options('liable_party')" :key="o.value" :value="o.value">
                      {{ o.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <!-- claim kind -->
            <div class="column is-6">
              <div class="field">
                <div class="control">
                  <label class="label">
                    ປະເພດຄວາມເສຍຫາຍ
                    <span class="text-danger">* {{ errors.first('claim_kind') }}</span>
                    <span class="text-danger">{{ server_errors.claim_kind }}</span>
                  </label>
                  <select class="input" name="claim_kind" v-model="form.claim_kind" v-validate="'required'">
                    <option value="">-- ເລືອກ --</option>
                    <option v-for="o in options('claim_kind')" :key="o.value" :value="o.value">
                      {{ o.label }}
                    </option>
                  </select>
                </div>
              </div>

              <!--
                ປະເພດເຄື່ອງ — what was IN the box, directly under what HAPPENED
                to it, because the two are read together and confused apart.

                Free text and optional: no list of contents exists in this
                application, and a parcel assessed before this field did has no
                answer that anyone should be made to invent.
              -->
              <div class="field">
                <div class="control">
                  <label class="label">
                    ປະເພດເຄື່ອງ
                    <span class="claim-note">(ບໍ່ບັງຄັບ)</span>
                    <span class="text-danger">{{ server_errors.goods_type }}</span>
                  </label>
                  <input
                    class="input"
                    name="goods_type"
                    v-model="form.goods_type"
                    maxlength="100"
                    placeholder="ຕົວຢ່າງ: ໂທລະສັບ, ເຄື່ອງນຸ່ງ, ເຄື່ອງແກ້ວ"
                  >
                </div>
              </div>
            </div>

            <!-- valuation basis -->
            <div class="column is-6">
              <div class="field">
                <div class="control">
                  <label class="label">
                    ຖານການຕີລາຄາ
                    <span class="text-danger">* {{ errors.first('valuation_basis') }}</span>
                    <span class="text-danger">{{ server_errors.valuation_basis }}</span>
                  </label>
                  <!--
                    On a non-COD bill there is exactly ONE basis this claim can
                    be valued on, so the control states it rather than asking.
                    Offering `cod` here would be offering a choice the submit
                    refuses (`claim_bill_has_no_cod`) — after the whole form is
                    filled in.
                  -->
                  <select
                    class="input"
                    name="valuation_basis"
                    v-model="form.valuation_basis"
                    v-validate="'required'"
                    :disabled="!hasCod"
                  >
                    <option value="" v-if="hasCod">-- ເລືອກ --</option>
                    <option v-for="o in basisOptions" :key="o.value" :value="o.value">
                      {{ o.label }}
                    </option>
                  </select>
                  <p class="claim-note" v-if="!hasCod">
                    ບິນນີ້ບໍ່ມີ COD — ຕີລາຄາຕາມໃບເສັດຊື້ເຄື່ອງເທົ່ານັ້ນ
                  </p>
                </div>
              </div>
            </div>

            <!-- amount -->
            <div class="column is-6">
              <div class="field">
                <div class="control">
                  <label class="label">
                    ຈຳນວນທີ່ອະນຸມັດ (ກີບ)
                    <span class="text-danger">* {{ errors.first('amount') }}</span>
                    <span class="text-danger">{{ server_errors.amount }}</span>
                  </label>
                  <!--
                    Typed into on the RECEIPT basis, and only there (owner
                    decision, 2026-10-12, reversing 2026-09-09). The officer who
                    inspected the parcel is the one who knows what it was worth,
                    and a figure they cannot correct turned every disagreement
                    into a refusal the claimant had to re-file.

                    The COD basis is still not this form's to give: the figure
                    comes off the BILL and the server replaces whatever is typed
                    here, so the field is `disabled` rather than merely readonly.

                    A GUARD, not the rule. `AssessClaimRequest` is what decides —
                    it has always accepted `amount` on the receipt basis.
                  -->
                  <ClaimMoneyInput
                    class="input"
                    name="amount"
                    data-vv-name="amount"
                    v-model="form.amount"
                    v-validate="isCodBasis ? '' : 'required|numeric|min_value:0'"
                    :disabled="isCodBasis"
                  />
                  <p class="claim-note" v-if="!isCodBasis">
                    ຕາມທີ່ລູກຄ້າຂໍ <strong>{{ formatMoney(parcel.claimed_amount) }} ກີບ</strong>
                  </p>
                  <!--
                    A changed figure is the one thing on this form a claimant will
                    argue about later, so the screen says so while it can still be
                    explained — in `fault_basis`, which is required anyway and is
                    where the decision's reasoning already lives.
                  -->
                  <p class="claim-note claim-note--warn" v-if="amountDiffersFromAsk">
                    ⚠️ ຈຳນວນນີ້ບໍ່ເທົ່າກັບທີ່ລູກຄ້າຂໍ —
                    ກະລຸນາຂຽນເຫດຜົນໃນຊ່ອງ “ຫຼັກຖານທີ່ໃຊ້ຕັດສິນຄວາມຮັບຜິດຊອບ” ດ້ານລຸ່ມ
                  </p>
                  <!--
                    On a COD basis the figure comes off the BILL, not off this
                    form — the backend ignores what is typed here. Saying so is
                    cheaper than an officer typing a number and watching it be
                    replaced by a different one.
                  -->
                  <p class="claim-note" v-if="isCodBasis">
                    ຖານ COD — ຈະອະນຸມັດຕາມບິນ
                    <strong>{{ formatMoney(codValuation) }} ກີບ</strong>
                    <span v-if="originFreight > 0">(COD + ຄ່າຂົນສົ່ງຕົ້ນທາງ)</span>
                    ບໍ່ໄດ້ມາຈາກຊ່ອງນີ້
                  </p>
                </div>
              </div>
            </div>

            <!--
              TWO registers, because they are two tables.

              The claim line used to believe a sorting centre was a `branches` row of type `central`
              and offered one list for both. It is not: the fifteen centres the company runs are
              `distribution_centers` rows and are absent from `branches` entirely, so what that one
              list offered under this heading were four administrative records — one of them
              ກວດສອບການເຄື່ອນໄຫວ, which is not a place. Parcels were recorded against it because it
              was the only answer the list allowed.

              ⚠️ There is deliberately NO control asking which KIND the chosen unit is. The KIND was
              answered UP THERE, in ຝ່າຍທີ່ຮັບຜິດຊອບ, before any unit was shown — so the list below
              is already narrowed to it and only one appears.

              That is a different thing from asking about a unit already named, which stays
              forbidden: that would be a second answer able to disagree with the first, and the
              disagreement decides whether somebody is billed.
            -->
            <div class="column is-12" v-if="isUnitParty">
              <span class="text-danger">{{ server_errors.liable_unit_id }}</span>

              <div class="field" v-if="isBranchParty">
                <div class="control">
                  <label class="label">ສາຂາ ທີ່ຮັບຜິດຊອບ</label>
                  <!-- The list is the branches the parcel passed through. An
                       officer can widen it to every branch when the responsible
                       one is off the recorded path. -->
                  <label class="checkbox is-size-7 mb-2" style="display:block">
                    <input type="checkbox" v-model="showAllBranches" @change="onToggleAllBranches" />
                    ສະແດງທຸກສາຂາ
                  </label>
                  <model-list-select
                    :value="branchChoice"
                    @input="chooseBranch"
                    :list="branchList"
                    option-text="name"
                    option-value="id"
                    selectedItem="name"
                    placeholder="ເລືອກສາຂາ...">
                  </model-list-select>
                </div>
              </div>

              <div class="field" v-if="isSortingCentreParty">
                <div class="control">
                  <label class="label">ສູນຄັດແຍກ ທີ່ຮັບຜິດຊອບ</label>
                  <!-- Fifteen rows, so no timeline narrowing and no "show all": there is nothing
                       to narrow. A centre excluded in the backend's configuration simply is not
                       here, and this screen is not rebuilt for that. -->
                  <!--
                    Keyed on `key`, NOT on `id`. A centre that splits its day appears twice
                    (ພາກເຊົ້າ / ພາກແລງ) and both lines carry the same `id` — a picker keyed on the
                    id would treat them as one option and could not tell which shift was chosen.
                    The key is the server's; this screen never builds or parses one.
                  -->
                  <model-list-select
                    :value="sortingCentreChoice"
                    @input="chooseSortingCentre"
                    :list="sortingCentreList"
                    option-text="name"
                    option-value="key"
                    selectedItem="name"
                    placeholder="ເລືອກສູນຄັດແຍກ...">
                  </model-list-select>
                  <p class="claim-note">ບໍ່ມີການກັ່ນຕອງຕາມເສັ້ນທາງຂອງພັດສະດຸ — ສູນທີ່ແບ່ງກະຈະມີໃຫ້ເລືອກທັງພາກເຊົ້າ ແລະ ພາກແລງ</p>
                </div>
              </div>
            </div>

            <!-- A vehicle line: the route AND the trip, because two lorries run the same route
                 on different days and only one of them carried this parcel. -->
            <div class="column is-7" v-if="isVehicleLineParty">
              <div class="field">
                <div class="control">
                  <label class="label">
                    ພາຫະນະສາຍ
                    <span class="text-danger">* {{ errors.first('liable_route') }}</span>
                    <span class="text-danger">{{ server_errors.liable_route }}</span>
                  </label>
                  <input
                    class="input"
                    type="text"
                    name="liable_route"
                    v-model="form.liable_route"
                    v-validate="'required|max:100'"
                    placeholder="ຕົວຢ່າງ: ວຽງຈັນ–ຫຼວງພະບາງ"
                  />
                </div>
              </div>
            </div>
            <div class="column is-5" v-if="isVehicleLineParty">
              <div class="field">
                <div class="control">
                  <label class="label">
                    ທ້ຽວອັນທີ
                    <span class="text-danger">* {{ errors.first('liable_trip_no') }}</span>
                    <span class="text-danger">{{ server_errors.liable_trip_no }}</span>
                  </label>
                  <input
                    class="input"
                    type="text"
                    name="liable_trip_no"
                    v-model="form.liable_trip_no"
                    v-validate="'required|max:50'"
                    placeholder="ຕົວຢ່າງ: 42"
                  />
                </div>
              </div>
            </div>

            <!--
              ຊົດເຊີຍຮ່ວມ — several units share this parcel.

              Ticks, not a picker: the question is "who was involved", and the company pays from
              one shared-case budget however many are. Naming the exact unit is OPTIONAL by the
              owner's decision — the detail goes into ຫຼັກຖານ below, which is required anyway —
              but when it IS named it is stored as a reference, because a column can be added
              later and six months of free text cannot be turned back into data.

              ⚠️ No amount per party. There is no money split: the parent company pays the
              claimant, and deducting from whoever was at fault happens afterwards, outside this
              system. A field here would invite an answer nobody acts on.
            -->
            <div class="column is-12" v-if="isSharedParty">
              <div class="field">
                <div class="control">
                  <label class="label">
                    ຝ່າຍທີ່ຮັບຜິດຊອບຮ່ວມກັນ
                    <span class="text-danger">{{ server_errors.shared_parties }}</span>
                  </label>

                  <div class="shared-grid">
                    <div v-for="row in visibleSharedRows" :key="row.kind" class="shared-row">
                      <label class="shared-tick">
                        <input type="checkbox" v-model="row.checked">
                        <span>{{ partyLabel(row.kind) }}</span>
                      </label>

                      <!-- ສາຂາ — one or MANY (owner, 2026-10-16): two branches can share one parcel.
                           Each chosen branch is its own party; none chosen is one unnamed branch. -->
                      <div class="shared-detail" v-if="row.checked && row.kind === 'branch'">
                        <multi-list-select
                          :list="allBranchList"
                          option-value="id"
                          option-text="name"
                          :selected-items="row.branches"
                          @select="(items) => { row.branches = items; }"
                          placeholder="ເລືອກສາຂາ — ເລືອກໄດ້ຫຼາຍສາຂາ (ບໍ່ບັງຄັບ)">
                        </multi-list-select>
                      </div>

                      <!-- ສູນຄັດແຍກ — the shift-aware lines, so ພາກເຊົ້າ and ພາກແລງ of one centre are
                           two choices here, exactly as they are two parties on the document. -->
                      <div class="shared-detail" v-if="row.checked && row.kind === 'sorting_centre'">
                        <multi-list-select
                          :list="sortingCentreList"
                          option-value="key"
                          option-text="name"
                          :selected-items="row.centres"
                          @select="(items) => { row.centres = items; }"
                          placeholder="ເລືອກສູນຄັດແຍກ — ເລືອກໄດ້ຫຼາຍສູນ (ບໍ່ບັງຄັບ)">
                        </multi-list-select>
                      </div>

                      <!-- ພາຫະນະສາຍ — route and trip, one row per line; both optional. -->
                      <div class="shared-detail" v-if="row.checked && row.kind === 'vehicle_line'">
                        <div class="shared-line" v-for="(line, i) in row.lines" :key="i">
                          <input class="input" v-model="line.route" maxlength="100" placeholder="ສາຍ (ບໍ່ບັງຄັບ)">
                          <input class="input" v-model="line.trip_no" maxlength="50" placeholder="ຖ້ຽວ (ບໍ່ບັງຄັບ)">
                          <button type="button" class="button is-small is-light"
                                  v-if="row.lines.length > 1"
                                  @click="row.lines.splice(i, 1)"
                                  title="ລຶບສາຍນີ້">
                            <i class="fas fa-trash"></i>
                          </button>
                        </div>
                        <button type="button" class="button is-small is-light shared-add"
                                @click="row.lines.push({ route: '', trip_no: '' })">
                          + ເພີ່ມສາຍ
                        </button>
                      </div>
                    </div>
                  </div>

                  <p class="claim-note" v-if="!sharedIsValid">
                    ⚠️ ຕ້ອງມີຢ່າງໜ້ອຍ 2 ຝ່າຍ (ເຊັ່ນ 2 ສາຂາ ຫຼື ສາຂາ + ສູນຄັດແຍກ) — ຖ້າມີຝ່າຍດຽວ ໃຫ້ເລືອກຝ່າຍນັ້ນໂດຍກົງຂ້າງເທິງແທນ
                  </p>
                  <p class="claim-note claim-note--warn" v-else-if="sharedHasDuplicateLine">
                    ⚠️ ມີພາຫະນະສາຍທີ່ຊ້ຳກັນ (ສາຍ ແລະ ຖ້ຽວ ຄືກັນ) — ກະລຸນາລຶບແຖວທີ່ຊ້ຳອອກ
                  </p>
                  <p class="claim-note" v-else>
                    ບໍລິສັດຈະຈ່າຍໃຫ້ລູກຄ້າກ່ອນຈາກງົບຊົດເຊີຍຮ່ວມ — ການຫັກຄືນຈາກຜູ້ທີ່ຜິດເຮັດພາຍຫຼັງ ນອກລະບົບນີ້
                  </p>
                </div>
              </div>
            </div>

            <!-- fault basis -->
            <div class="column is-12">
              <div class="field">
                <div class="control">
                  <label class="label">
                    ຫຼັກຖານທີ່ໃຊ້ຕັດສິນຄວາມຮັບຜິດຊອບ
                    <span class="text-danger">* {{ errors.first('fault_basis') }}</span>
                    <span class="text-danger">{{ server_errors.fault_basis }}</span>
                  </label>
                  <textarea
                    class="input claim-textarea"
                    name="fault_basis"
                    rows="3"
                    v-model="form.fault_basis"
                    v-validate="'required|max:500'"
                    placeholder="ຕົວຢ່າງ: ຮູບຕອນຮັບເຂົ້າສາຂາ ສະແດງວ່າກ່ອງຍັງດີ ແລະ ເສຍຫາຍຫຼັງຈາກນັ້ນ"
                  ></textarea>
                  <!--
                    Required by the backend on purpose (its design D18). The
                    branch no longer reads it to decide whether to appeal —
                    there is no appeal here any more — but it is what the
                    recovery is argued from wherever that argument happens, and
                    it is the record of why this party was named at all.
                  -->
                  <p class="claim-note">
                    ເຫດຜົນນີ້ຈະຕິດຢູ່ກັບຄຳຮ້ອງຖາວອນ — ເປັນຫຼັກຖານວ່າເປັນຫຍັງຈຶ່ງລະບຸຝ່າຍນີ້
                  </p>
                </div>
              </div>
            </div>

            <!--
              The artefact the paragraph above CITES. Its own placeholder asks
              the officer to reference a photograph ("the intake photo shows the
              box was still sound") and there was nowhere to put it, so the
              branch argued its appeal against a sentence.

              Optional on purpose: most assessments are made from files the
              claimant already attached, and requiring an upload would only get
              those same files uploaded a second time to satisfy the form.
            -->
            <div class="column is-12">
              <claim-file-picker
                ref="picker"
                label="ຮູບ / ເອກະສານປະກອບການປະເມີນ (ບໍ່ບັງຄັບ)"
                hint="ລູກຄ້າຈະບໍ່ເຫັນໄຟລ໌ນີ້ — ເຫັນສະເພາະພະນັກງານ ແລະ ສາຂາທີ່ຮັບຜິດຊອບ"
                :max-file-bytes="maxFileBytes"
                @input="evidenceFiles = $event"
              />
            </div>
          </div>

          <!--
            The fork, made legible before it is taken (task 2.5). These two
            answers send the claim down paths that behave differently for weeks,
            and the difference is not visible from the words "branch" and
            "sorting centre" alone.
          -->
          <!--
            Both answers send the claimant down the same road: the company pays, through the ERP.
            What the officer is choosing here is who carries the cost afterwards, and saying so is
            the point — the screen used to imply that naming a branch handed the customer away.
          -->
          <div class="claim-fork" v-if="form.liable_party">
            <div v-if="isRecoverableParty" class="notification-line is-warn">
              <strong>ບໍລິສັດຈ່າຍໃຫ້ລູກຄ້າກ່ອນ:</strong>
              ສ້າງເອກະສານເຂົ້າ ERP ຄືກັນທຸກໃບ — ລູກຄ້າບໍ່ຕ້ອງລໍຖ້າການຖົກຖຽງພາຍໃນ.
              ຫຼັງອະນຸມັດແລ້ວ ERP ຈະອອກໃບຕິດຕາມທວງຄືນຈາກຝ່າຍທີ່ຮັບຜິດຊອບ.
            </div>
            <div v-else class="notification-line is-info">
              <strong>ຄ່າໃຊ້ຈ່າຍຂອງບໍລິສັດເອງ:</strong>
              ສ້າງເອກະສານເຂົ້າ ERP ແລະ ກັນງົບປະມານປະຈຳປີໄວ້ທັນທີ. ບໍ່ມີການທວງຄືນ.
              ການອະນຸມັດເຮັດຢູ່ໃນ ERP ໂດຍຜູ້ມີສິດຂອງເຂົາ ບໍ່ແມ່ນທີ່ນີ້.
            </div>
          </div>

          <div class="claim-submit">
            <button class="button is-primary" :disabled="saving" @click="submit">
              {{ saving ? 'ກຳລັງບັນທຶກ...' : 'ບັນທຶກຜົນການປະເມີນ' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ModelListSelect, MultiListSelect } from 'vue-search-select';
import ClaimApi from '@/Mixins/Claim/ClaimApi';
import ClaimFilePicker from './components/ClaimFilePicker.vue';
import ClaimMoneyInput from './components/ClaimMoneyInput.vue';

/**
 * The assessment — fault, kind, basis and value decided TOGETHER, in one submit.
 *
 * The legacy line took `status` as an input and did five different jobs behind
 * `required_if` rules; this screen is deliberately the opposite shape. Nothing
 * here is a partial save: the backend takes all five fields at once because they
 * are one decision, and splitting them into steps would let a claim exist with a
 * fault location and no basis for it.
 */
export default {
  name: 'ClaimAssess',
  // 953 branches — a `select` is unusable at that length, so the same searchable
  // control the queue's branch filter uses. Registered locally like every other
  // screen in this app; it is not global.
  components: { ModelListSelect, MultiListSelect, ClaimFilePicker, ClaimMoneyInput },
  mixins: [ClaimApi],

  data: () => ({
    claim: null,
    /**
     * The box being assessed.
     *
     * Assessment is a decision about a parcel, not about a case: two boxes on one claim can be
     * lost at different places and carried by different parties, and the party is what chooses the
     * ERP document each of them is raised as. The detail screen names which one in the query.
     */
    parcel: null,
    // The picker's two sources. By default it offers only the branches the
    // parcel passed through (`timelineBranchList`); `allBranchList` is the full
    // ~811-branch list, loaded for the fallback and the "show all" toggle.
    timelineBranchList: [],
    allBranchList: [],
    showAllBranches: false,
    /** The other register. Fifteen rows, so it is fetched whole and never narrowed. */
    sortingCentreList: [],
    saving: false,
    /** Internal evidence to upload alongside the assessment. */
    evidenceFiles: [],
    /**
     * ຊົດເຊີຍຮ່ວມ — one row per party the assessor may tick.
     *
     * Fixed four rows rather than an add/remove list: the parties are a closed set, and a list
     * the officer builds invites the same party twice — which the server refuses, after they
     * have already filled it in.
     */
    /**
     * The parties a ຊົດເຊີຍຮ່ວມ set may name.
     *
     * ⚠️ ບໍລິສັດ is NOT here (owner, 2026-10-14). A shared case names who the money will be
     * recovered FROM, and the company is who recovers — ticking it put a party on the document
     * nobody would ever be asked for a kip. A loss the company carries alone is the ordinary
     * assessment, which is a different answer on this same form. The server refuses `company`
     * here, so a row left in would be a tick that fails at submit with nothing on the form to
     * say why.
     *
     * Rendered through `visibleSharedRows`, which filters this against the list the API
     * publishes — these objects stay the ones `v-model` writes to.
     *
     * One tick per KIND, and under it as many units as were really involved (owner, 2026-10-16):
     * `branches` / `centres` hold the multi-select's chosen options, `lines` the vehicle rows.
     * `sharedPayload` flattens them into one party per unit.
     */
    sharedRows: [
      { kind: 'branch', checked: false, branches: [], centres: [], lines: [{ route: '', trip_no: '' }] },
      { kind: 'sorting_centre', checked: false, branches: [], centres: [], lines: [{ route: '', trip_no: '' }] },
      { kind: 'vehicle_line', checked: false, branches: [], centres: [], lines: [{ route: '', trip_no: '' }] },
    ],
    form: {
      liable_party: '',
      claim_kind: '',
      goods_type: '',
      valuation_basis: '',
      amount: '',
      fault_basis: '',
      liable_unit_id: null,
      /**
       * ພາກເຊົ້າ / ພາກແລງ — set by WHICH line of the picker was chosen, never typed.
       * Null for a branch, for a vehicle line, and for a centre that does not split its day.
       */
      liable_unit_shift: null,
      /**
       * WHICH register the chosen unit came out of — `branch` or `distribution_center`.
       *
       * Not a question the assessor answers: it is set by whichever list they used. The two
       * registers number their rows independently, so an id sent without this resolves against
       * whichever table the server asks and yields a different unit's name with nothing to signal
       * the error.
       */
      liable_unit_type: null,
      liable_route: '',
      liable_trip_no: '',
    },
  }),

  computed: {
    /**
     * Does the answer name a unit?
     *
     * Four answers now: `company`, `branch`, `sorting_centre`, `vehicle_line`. Two of them name a
     * unit, and WHICH of the two fixes the register the list below is drawn from — the kind of
     * place, asked before the place.
     *
     * `unit` is the older answer, still accepted by the API and still rendered here so a screen
     * seeded from a claim assessed under it does not come up blank. It is not offered: `/config`
     * publishes the four.
     */
    isBranchParty() {
      return this.form.liable_party === 'branch'
        || (this.form.liable_party === 'unit' && this.form.liable_unit_type !== 'distribution_center');
    },

    isSortingCentreParty() {
      return this.form.liable_party === 'sorting_centre'
        || (this.form.liable_party === 'unit' && this.form.liable_unit_type === 'distribution_center');
    },

    isUnitParty() {
      return this.isBranchParty || this.isSortingCentreParty;
    },

    isVehicleLineParty() {
      return this.form.liable_party === 'vehicle_line';
    },

    /**
     * Whether what is chosen leaves something to recover. The company itself never does; a unit
     * might, and WHICH it is depends on that unit's type — a question only the server can answer,
     * so the banner speaks of a possibility, not a fact.
     */
    isRecoverableParty() {
      return this.isUnitParty || this.isVehicleLineParty;
    },

    /**
     * What the picker offers: the parcel's timeline by default, the full list
     * when the officer asks for it (or when the timeline came back empty and the
     * fallback flipped the toggle on).
     */
    branchList() {
      return this.showAllBranches ? this.allBranchList : this.timelineBranchList;
    },

    /**
     * The chosen unit, shown in the list it belongs to and blank in the other.
     *
     * Deriving both from one `liable_unit_id` + its register is what makes "one unit, two lists"
     * true rather than decorative: there is a single value underneath, so the two controls cannot
     * both hold a choice.
     */
    branchChoice() {
      return this.form.liable_unit_type === 'branch' ? this.form.liable_unit_id : null;
    },

    /**
     * The chosen option's KEY, rebuilt from the pair the form holds.
     *
     * Rebuilt rather than stored so that a parcel loaded for RE-assessment selects the right line
     * on its own: the server gives back `liable_unit_id` and `liable_unit_shift`, and the option
     * that matches both is the one the picker must show.
     */
    sortingCentreChoice() {
      if (this.form.liable_unit_type !== 'distribution_center' || !this.form.liable_unit_id) return null;

      const match = this.sortingCentreList.find(
        (o) => Number(o.id) === Number(this.form.liable_unit_id)
          && (o.shift || null) === (this.form.liable_unit_shift || null),
      );

      return match ? match.key : null;
    },

    isSharedParty() {
      return this.form.liable_party === 'shared';
    },

    /**
     * The rows this deployment's API actually accepts.
     *
     * The server publishes `shared_parties`; this follows it rather than restating it, so the day
     * the office adds or removes a party the screen moves with the rule instead of a release
     * later. Falls back to the local list when the key is absent — an older backend must not
     * render an empty question.
     */
    visibleSharedRows() {
      const allowed = (this.options('shared_parties') || []).map((o) => o.value);

      return allowed.length
        ? this.sharedRows.filter((r) => allowed.includes(r.kind))
        : this.sharedRows;
    },

    /**
     * The ticked rows, in the shape the API is sent — ONE PARTY PER UNIT.
     *
     * Two branches chosen are two parties, and both reach the ERP's ຝ່າຍທີ່ຮັບຜິດຊອບຮ່ວມ. A kind
     * ticked with nothing named is still one party (naming is optional), sent once. Blank vehicle
     * rows beside filled ones are dropped: an empty line next to a named route says nothing.
     */
    sharedPayload() {
      const party = (kind, extra) => ({
        liable_party_kind: kind,
        liable_unit_id: null,
        liable_unit_shift: null,
        liable_route: null,
        liable_trip_no: null,
        ...extra,
      });

      return this.visibleSharedRows
        .filter((r) => r.checked)
        .flatMap((r) => {
          if (r.kind === 'branch') {
            return r.branches.length
              ? r.branches.map((b) => party('branch', { liable_unit_id: b.id }))
              : [party('branch')];
          }

          if (r.kind === 'sorting_centre') {
            return r.centres.length
              ? r.centres.map((c) => party('sorting_centre', { liable_unit_id: c.id, liable_unit_shift: c.shift || null }))
              : [party('sorting_centre')];
          }

          const named = r.lines
            .map((l) => ({ route: (l.route || '').trim(), trip_no: (l.trip_no || '').trim() }))
            .filter((l) => l.route || l.trip_no);

          return named.length
            ? named.map((l) => party(r.kind, { liable_route: l.route || null, liable_trip_no: l.trip_no || null }))
            : [party(r.kind)];
        });
    },

    /**
     * The same route and trip entered twice. The server refuses it as one party listed twice; the
     * screen says so first, and says which field to fix.
     */
    sharedHasDuplicateLine() {
      const keys = this.sharedPayload
        .filter((p) => p.liable_party_kind === 'vehicle_line')
        .map((p) => `${p.liable_route || ''}|${p.liable_trip_no || ''}`);

      return new Set(keys).size !== keys.length;
    },

    /**
     * Two or more, which is what makes it SHARED.
     *
     * The server enforces this too; the screen says it first so an officer does not fill the
     * rest of the form and be refused at the end.
     */
    sharedIsValid() {
      return this.sharedPayload.length >= 2 && !this.sharedHasDuplicateLine;
    },

    /** The one basis where the amount is not this form's to give. */
    isCodBasis() {
      return this.form.valuation_basis === 'cod';
    },

    /**
     * Is the officer approving something other than what was asked for?
     *
     * Only on the receipt basis — on COD the figure is the bill's and a
     * difference from the ask is normal, not a decision anybody made here.
     * Compared as NUMBERS on purpose: the ask arrives as the decimal string the
     * whole line uses (`"450000.00"`) and the input gives back what was typed,
     * so `450000` and `450000.00` are the same answer and must not warn.
     */
    amountDiffersFromAsk() {
      if (this.isCodBasis || !this.parcel) return false;

      const asked = Number(this.parcel.claimed_amount);
      const approved = Number(this.form.amount);

      if (!Number.isFinite(asked) || !Number.isFinite(approved)) return false;

      return approved !== asked;
    },

    /** The server's upload ceiling, from `/config` — not a guess made here. */
    maxFileBytes() {
      return this.$store.getters['Claim/maxFileBytes'];
    },

    /** The bill's declared COD, or null when it carries none. */
    codAmount() {
      return this.parcel && this.parcel.shipment ? this.parcel.shipment.cod_amount : null;
    },

    /** Freight the sender already paid at the origin on a COD bill (0 when none). */
    originFreight() {
      return Number(this.parcel && this.parcel.shipment ? this.parcel.shipment.origin_freight : 0) || 0;
    },

    /** What the COD basis pays — COD plus the origin-paid freight; the backend writes the same. */
    codValuation() {
      const shipment = this.parcel && this.parcel.shipment;
      if (shipment && shipment.cod_valuation !== undefined && shipment.cod_valuation !== null) {
        return shipment.cod_valuation;
      }
      return this.codAmount;
    },

    /** Which of the claim's boxes this is, for a header that has to say so. */
    parcelPosition() {
      if (!this.claim || !this.parcel) return 1;

      return this.claim.items.findIndex((i) => i.id === this.parcel.id) + 1;
    },

    /**
     * Whether the COD basis is available AT ALL.
     *
     * Null means no COD payment on the bill; `0` means one for nothing. Neither
     * can be valued on COD, and the backend refuses both — so both close the
     * option here rather than at submit time.
     */
    hasCod() {
      return Number(this.codAmount) > 0;
    },

    /**
     * Both bases on a COD bill, receipt alone otherwise. Derived from the
     * published vocabulary, never a hardcoded pair — the labels and the set are
     * still the server's (`/claims/config`).
     */
    basisOptions() {
      const all = this.options('valuation_basis');
      return this.hasCod ? all : all.filter((o) => o.value !== 'cod');
    },

    /**
     * What will actually be approved. On a COD basis the amount field is
     * disabled and empty, but the claim is still worth the bill's COD — reading
     * the blank field instead told the officer "no sign-off needed" on a claim
     * that needs one.
     */
    effectiveAmount() {
      return (this.isCodBasis ? this.codValuation : this.form.amount) || 0;
    },
  },

  created() {
    this.$store.dispatch('Claim/load').finally(() => {
      // Branches depend on the claim's shipment now, so they load AFTER the
      // claim rather than alongside it.
      this.fetchClaim().then(() => this.fetchBranches());
    });
  },

  methods: {
    options(group) {
      return this.$store.getters['Claim/options'](group);
    },

    /**
     * `cod` when the bill has one, `receipt` when it does not — but only for a
     * value the vocabulary actually publishes, so a server that renames or drops
     * a basis leaves the field empty (and required) rather than pre-filled with
     * a word this file invented.
     */
    defaultBasis() {
      const wanted = this.hasCod ? 'cod' : 'receipt';
      return this.options('valuation_basis').some((o) => o.value === wanted) ? wanted : '';
    },

    fetchClaim() {
      return this.claimDetail(this.$route.params.id)
        .then((res) => {
          this.claim = res.data.data;

          // WHICH parcel is being assessed. Named in the query by the detail screen's per-parcel
          // button; on a claim of one — every claim written before this change — it is the only
          // one there is. A claim of several reached without one would be an assessment about a box
          // nobody chose, so it refuses rather than picking the first.
          const wanted = Number(this.$route.query.item);
          const items = this.claim.items || [];

          this.parcel = wanted
            ? items.find((i) => i.id === wanted)
            : (items.length === 1 ? items[0] : null);

          if (!this.parcel) {
            this.$notification.error({ message: 'ບໍ່ໄດ້ລະບຸວ່າຈະປະເມີນພັດສະດຸລາຍການໃດ' });
            this.$router.push({ name: 'claim.detail', params: { id: this.claim.id } });
            return;
          }

          // Seed from what is already known. The claimant's kind is a starting
          // point for the officer's, not a decision already made.
          //
          // The amount is different: what we approve IS what they asked for
          // (owner decision, 2026-09-09). It is seeded from `claimed_amount`
          // rather than from any earlier assessment, so a figure typed before
          // this rule existed does not survive a re-assessment.
          this.form.claim_kind = this.parcel.claim_kind || '';
          this.form.goods_type = this.parcel.goods_type || '';
          // A claim already assessed comes back with the CONCLUSION (four kinds); this form asks
          // the QUESTION (three answers), so a sorting centre and a branch both seed "a unit".
          const party = this.parcel.liable_party;
          if (party) {
            this.form.liable_party = party.kind === 'vehicle_line'
              ? 'vehicle_line'
              : (party.kind === 'company' ? 'company' : 'unit');
            this.form.liable_route = party.vehicle_line ? party.vehicle_line.route : '';
            this.form.liable_trip_no = party.vehicle_line ? party.vehicle_line.trip_no : '';
          }
          // Default to COD when the bill has one — it is the customer's own
          // declared value and the ops flow prefers it over a receipt. On a bill
          // without one there is no choice to make, so it is fixed rather than
          // defaulted. A basis already decided by an earlier assessment wins
          // over both: re-assessing must not silently move it.
          this.form.valuation_basis = this.parcel.valuation_basis || this.defaultBasis();
          this.form.amount = this.parcel.claimed_amount || '';
          this.form.fault_basis = this.parcel.fault_basis || '';
          // Re-opening an assessed parcel seeds the list the unit actually came from, so the
          // officer sees their own earlier choice where they made it.
          this.form.liable_unit_id = party && party.unit ? party.unit.id : null;
          this.form.liable_unit_type = party && party.unit
            ? (party.unit.register || 'branch')
            : null;
          // The shift the earlier assessment recorded, so the picker re-selects the same LINE
          // and not merely the same centre.
          this.form.liable_unit_shift = party && party.unit ? (party.unit.shift || null) : null;
          this.prefillSharedRows(party ? party.shared : null);
        })
        .catch((error) => this.handleActionError(error));
    },

    /**
     * Load the parcel's timeline branches first. If the path is unrecorded (an
     * empty list) or the read fails, fall back to the full list so the picker is
     * never empty on a field that is not optional.
     */
    fetchBranches() {
      if (!this.claim || !this.parcel) return Promise.resolve();

      // The other register, loaded beside this one: both are needed the moment the unit question
      // is answered, and neither narrows the other.
      this.loadSortingCentres();
      // ຊົດເຊີຍຮ່ວມ picks a branch from the FULL list, not the parcel's timeline: a unit that
      // shares responsibility need not be one the parcel passed through — a wrongly-routed box
      // is the fault of a branch it never reached.
      this.loadAllBranches();

      return this.claimTimelineBranches(this.claim.id, this.parcel.id)
        .then((res) => {
          this.timelineBranchList = res.data.data || [];
          if (!this.timelineBranchList.length) {
            this.showAllBranches = true;
            return this.loadAllBranches();
          }
        })
        .catch(() => {
          this.showAllBranches = true;
          return this.loadAllBranches();
        });
    },

    /**
     * Choosing in one list clears the other. A claim names ONE unit.
     *
     * The register is recorded here, from which control was used — never asked.
     */
    /**
     * Changing the KIND of place clears the place.
     *
     * Without this, answering ສາຂາ, choosing one, then switching to ສູນຄັດແຍກ would submit a branch
     * id under the sorting-centre answer — and the ids overlap, so the server would resolve it to a
     * real centre nobody chose. The one case where the two registers colliding could actually bite.
     */
    onLiablePartyChanged() {
      this.form.liable_unit_id = null;
      this.form.liable_unit_type = null;
      // Cleared with the unit: the server REFUSES a shift on anything but a sorting centre, so a
      // leftover one turns the next submit into a 422 on a field the officer cannot see.
      this.form.liable_unit_shift = null;
      this.resetSharedRows();
      this.form.liable_route = '';
      this.form.liable_trip_no = '';
    },

    chooseBranch(id) {
      this.form.liable_unit_id = id || null;
      this.form.liable_unit_type = id ? 'branch' : null;
      // A branch has no shift. Cleared here too because the older `unit` answer lets an officer
      // move between the two registers without `onLiablePartyChanged` ever firing.
      this.form.liable_unit_shift = null;
    },

    /**
     * `key` identifies the LINE; the id and the shift are read off it.
     *
     * The pair is what the server is sent — it never receives the key, so no endpoint has to
     * parse a composite string a client assembled.
     */
    /** Every tick cleared. Called whenever the answer stops being ຊົດເຊີຍຮ່ວມ. */
    resetSharedRows() {
      this.sharedRows.forEach((r) => {
        r.checked = false;
        r.branches = [];
        r.centres = [];
        r.lines = [{ route: '', trip_no: '' }];
      });
    },

    /**
     * Re-opening a shared parcel re-ticks what it was assessed with.
     *
     * Matched by KIND, because the rows are fixed and the server returns the set in no particular
     * order. A kind this screen no longer offers — `company`, on a parcel assessed before
     * 2026-10-14 — simply finds no row and is skipped, which is what should happen: the old answer
     * is history and re-saving it is not allowed.
     */
    prefillSharedRows(shared) {
      this.resetSharedRows();

      const lines = [];

      // EVERY entry of a kind goes back under its tick. Matching only the first one per kind used
      // to be enough; with several branches it would silently drop the second on re-save.
      (shared || []).forEach((entry) => {
        const row = this.sharedRows.find((r) => r.kind === entry.kind);
        if (!row) return;

        row.checked = true;

        if (!entry.unit && !entry.vehicle_line) return;

        if (entry.kind === 'branch') {
          row.branches.push({ id: entry.unit.id, name: entry.unit.display_name || String(entry.unit.id) });
        } else if (entry.kind === 'sorting_centre') {
          // The picker selects by key; rebuild the one that matches this id and shift.
          const key = `${entry.unit.id}${entry.unit.shift ? `:${entry.unit.shift}` : ''}`;
          row.centres.push(
            this.sortingCentreList.find((o) => o.key === key)
              || { key, id: entry.unit.id, shift: entry.unit.shift || null, name: entry.unit.display_name || key },
          );
        } else if (entry.kind === 'vehicle_line') {
          lines.push({ route: entry.vehicle_line.route || '', trip_no: entry.vehicle_line.trip_no || '' });
        }
      });

      const vehicle = this.sharedRows.find((r) => r.kind === 'vehicle_line');
      if (vehicle && lines.length) vehicle.lines = lines;
    },

    /** The published label for a party kind, from `/config` — never spelled here. */
    partyLabel(kind) {
      const found = (this.options('liable_party_kind') || []).find((o) => o.value === kind);

      return found ? found.label : kind;
    },

    chooseSortingCentre(key) {
      const option = key ? this.sortingCentreList.find((o) => o.key === key) : null;

      this.form.liable_unit_id = option ? option.id : null;
      this.form.liable_unit_type = option ? 'distribution_center' : null;
      this.form.liable_unit_shift = option ? (option.shift || null) : null;
    },

    /**
     * The sorting centres. Fifteen rows, fetched whole.
     *
     * A failure leaves the list empty, which is exactly what an assessor had before this existed —
     * so the degraded state is today's behaviour rather than a broken screen. The branch side is
     * unaffected either way.
     */
    loadSortingCentres() {
      if (this.sortingCentreList.length) return Promise.resolve();

      return this.claimSortingCentres()
        .then((res) => {
          this.sortingCentreList = res.data.data || [];
        })
        .catch(() => {
          this.sortingCentreList = [];
        });
    },

    /** The full ~811-branch list, fetched once and reused by the fallback and toggle. */
    loadAllBranches() {
      if (this.allBranchList.length) return Promise.resolve();

      return this.claimBranches()
        .then((res) => {
          this.allBranchList = res.data.data || [];
        })
        .catch(() => {
          this.allBranchList = [];
        });
    },

    /** The officer asked to see every branch (or to go back to the timeline). */
    onToggleAllBranches() {
      if (this.showAllBranches) this.loadAllBranches();
    },

    /**
     * The evidence, before the decision it supports.
     *
     * Ordered this way because the failure has to be recoverable: an assessment
     * saved with its upload lost is a decision on the record whose stated basis
     * is missing, and the officer has already navigated away. Uploading first
     * means a failure leaves the form exactly as it was, still fillable.
     */
    uploadEvidence() {
      if (!this.evidenceFiles.length) return Promise.resolve();

      // Against the PARCEL: the material a fault decision was made from is about one box, and the
      // branch's appeal is argued from exactly this.
      return this.claimUploadAttachment(this.claim.id, 'assessment_evidence', this.evidenceFiles, this.parcel.id);
    },

    submit() {
      this.$validator.validateAll().then((ok) => {
        if (!ok) return;
        // The picker owns the oversize refusal; submitting past it would save
        // the decision and silently drop the file it cites.
        if (this.$refs.picker && this.$refs.picker.fileError) return;

        this.saving = true;
        this.server_errors = {};

        const payload = {
          liable_party: this.form.liable_party,
          claim_kind: this.form.claim_kind,
          // Blank means "not answered" and must reach the server as null, not as
          // "": the column is nullable and an empty string is a value that would
          // read as an answer nobody gave.
          goods_type: this.form.goods_type ? this.form.goods_type : null,
          valuation_basis: this.form.valuation_basis,
          // Sent as typed. Money is a string end to end on this line; parsing it
          // here is how a decimal becomes a float and a fully-paid claim later
          // refuses to close.
          amount: this.isCodBasis ? null : this.form.amount,
          fault_basis: this.form.fault_basis,
          // Only the field the chosen party has. The server refuses the others outright, which is
          // what keeps a company-borne claim from carrying a unit nobody meant to name.
          liable_unit_id: this.isUnitParty ? this.form.liable_unit_id : null,
          // Only where it can exist. The API prohibits it on a branch or a vehicle line, and
          // sending null there is the difference between "no shift" and a refused request.
          liable_unit_shift: this.isSortingCentreParty ? this.form.liable_unit_shift : null,
          // Sent ONLY on the shared answer. The API prohibits it beside any other, so an array
          // left over from a changed answer would turn the submit into a 422.
          ...(this.isSharedParty ? { shared_parties: this.sharedPayload } : {}),
          // ⚠️ `liable_unit_type` is NOT sent. The answer above already fixed the register, and the
          // API refuses it alongside `branch` / `sorting_centre` precisely so nothing can state the
          // same thing twice and disagree with itself.
          liable_route: this.isVehicleLineParty ? this.form.liable_route : null,
          liable_trip_no: this.isVehicleLineParty ? this.form.liable_trip_no : null,
        };

        this.uploadEvidence()
          .then(() => this.claimAssessParcel(this.claim.id, this.parcel.id, payload))
          .then(() => {
            this.$notification.success({ message: 'ບັນທຶກຜົນການປະເມີນແລ້ວ' });
            this.$router.push({ name: 'claim.detail', params: { id: this.claim.id } });
          })
          .catch((error) => this.handleActionError(error))
          .finally(() => {
            this.saving = false;
          });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.claim-of {
  font-size: 13px;
  font-weight: 400;
  color: #7a7a7a;
  margin-left: 8px;
}

.claim-section {
  font-weight: 700;
  margin-bottom: 10px;
}

.claim-asked {
  color: #7a7a7a;
}

.claim-muted {
  color: #7a7a7a;
}

.claim-textarea {
  height: auto;
  padding: 8px 10px;
}

.notification-line {
  padding: 10px 14px;
  border-radius: 4px;
  margin-bottom: 10px;

  &.is-info { background: #eaf4fd; color: #1f5f8b; }
  &.is-warn { background: #fdf6e3; color: #8a6d3b; }
}

.claim-fork {
  margin-top: 12px;
}

.claim-note {
  font-size: 12px;
  color: #7a7a7a;
  margin-top: 6px;
}

/* ຊົດເຊີຍຮ່ວມ. One line per party so a tick and its optional detail read together. */
.shared-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.shared-row {
  display: grid;
  grid-template-columns: 180px 1fr;
  align-items: start;
  gap: 10px;
}

.shared-tick {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

/* ພາຫະນະສາຍ — one route/trip pair per line, a delete button when there is more than one. */
.shared-line {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 8px;
  margin-bottom: 8px;
}

.shared-add {
  margin-top: 2px;
}

@media (max-width: 768px) {
  .shared-row { grid-template-columns: 1fr; }
  .shared-line { grid-template-columns: 1fr 1fr auto; }
}

/* A figure the claimant did not ask for. Loud enough to be read before submit,
   not an error — changing it is allowed, explaining it is what is asked. */
.claim-note--warn {
  color: #946c00;
  font-weight: 600;
}

.claim-submit {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
}
</style>
